import Phaser from 'phaser';
import type { Save } from '../../meta/save/schema';
import { adoptSave, decodeSaveCode, encodeSaveCode, saveSummary } from '../../meta/saveCode';
import { haptics } from '../../platform/haptics';
import { pasteText, shareText } from '../../platform/share';
import { gameAudio } from '../audio/audio';
import { saves } from '../records';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { label, UI } from '../ui/text';
import { wrapLineCount } from '../ui/wrap';
import { wipeTo } from './WipeScene';

const HOW = [
  'Take your lawns, coins and mowers to another device, or from the website to the app.',
  'Here: SEND MY SAVE CODE, to yourself (or copy it).',
  'There: Settings, MOVE PROGRESS, PASTE A SAVE CODE.',
  'The full game is not in the code: on the new device, use RESTORE.',
];

const summaryLine = (s: Readonly<Save>): string => {
  const { lawns, stars, coins } = saveSummary(s);
  return `${lawns} lawn${lawns === 1 ? '' : 's'}, ${stars} stars, ${coins} coins`;
};

/**
 * Move progress (lm-ifl.12): a save code sent from one device and pasted on another, with no
 * server (src/meta/saveCode.ts). Pasting asks before it replaces anything.
 */
export class ProgressScene extends Phaser.Scene {
  private note: { text: string; color: number } | null = null;
  /** A pasted code's save, waiting for the player to confirm the swap. */
  private pending: Save | null = null;
  private done = false;

  constructor() {
    super('Progress');
  }

  init(): void {
    this.note = null;
    this.pending = null;
    this.done = false;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const g = this.add.graphics();
    g.fillStyle(UI.panel).fillRect(0, 0, width, 22);
    g.fillStyle(UI.rule).fillRect(0, 22, width, 1);
    label(this, 6, 4, 'MOVE PROGRESS', 2);

    const textW = width - 28;
    const lines: { text: string; color: number }[] = this.pending
      ? [
          { text: "Replace this device's progress with the code's?", color: UI.accent },
          { text: `The code: ${summaryLine(this.pending)}`, color: UI.text },
          { text: `This device: ${summaryLine(saves().save)}`, color: UI.dim },
          { text: "This device's progress will be gone.", color: UI.dim },
        ]
      : this.done
        ? [{ text: 'Progress moved! Welcome back.', color: UI.accent }]
        : HOW.map((text, i) => ({ text, color: i === 0 ? UI.text : UI.dim }));
    // The code carries the online player (docs/ONLINE.md): whoever has it can post as them. Said
    // when there is room above the buttons (a short iPad screen goes without).
    const keep = {
      text: 'Keep the code to yourself: it carries your online name.',
      color: UI.accent,
    };
    const used = (ls: readonly { text: string }[]) =>
      34 + ls.reduce((h, l) => h + wrapLineCount(l.text, textW) * 10 + 4, 0) + 8;
    const roomy = used([...lines, keep]) <= height - 30 - 56 - 8;
    if (!this.pending && !this.done && saves().save.online.player && roomy) lines.push(keep);
    let y = 34;
    const panelH = lines.reduce((h, l) => h + wrapLineCount(l.text, textW) * 10 + 4, 0);
    navyPanel(g, 4, y - 6, width - 8, panelH + 8);
    for (const l of lines) {
      label(this, 14, y, l.text, 1, l.color).setMaxWidth(textW);
      y += wrapLineCount(l.text, textW) * 10 + 4;
    }
    y += 8;
    if (this.note) {
      label(this, width / 2, y, this.note.text, 1, this.note.color)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
    }

    // Buttons at the bottom, in thumb reach.
    const backY = height - 30;
    if (this.pending) {
      const incoming = this.pending;
      bevelButton(
        this,
        6,
        backY - 34,
        width - 12,
        28,
        'REPLACE',
        () => this.adopt(incoming),
        true,
        2,
      );
      bevelButton(this, 6, backY, width - 12, 24, 'CANCEL', () => {
        this.pending = null;
        this.redraw();
      });
      return;
    }
    if (this.done) {
      bevelButton(this, 6, backY, width - 12, 24, 'TO THE LAWNS', () => wipeTo(this, 'Menu'), true);
      return;
    }
    bevelButton(this, 6, backY - 56, width - 12, 22, 'SEND MY SAVE CODE', () => void this.send());
    bevelButton(this, 6, backY - 30, width - 12, 22, 'PASTE A SAVE CODE', () => void this.paste());
    bevelButton(this, 6, backY, width - 12, 24, 'BACK', () => wipeTo(this, 'Settings'));
  }

  private async send(): Promise<void> {
    const outcome = await shareText('lawnmower. save code', encodeSaveCode(saves().save));
    this.note =
      outcome === 'copied'
        ? { text: 'Copied. Paste it on the other device.', color: UI.accent }
        : outcome === 'failed'
          ? { text: "Couldn't share or copy the code.", color: UI.dim }
          : null;
    this.redraw();
  }

  private async paste(): Promise<void> {
    const text = await pasteText();
    if (!text) {
      this.note = { text: 'Nothing to paste. Copy the code first.', color: UI.dim };
      this.redraw();
      return;
    }
    const result = decodeSaveCode(text);
    if (result.ok) {
      this.pending = result.save;
      this.note = null;
    } else {
      this.note = { text: result.problem, color: UI.dim };
    }
    this.redraw();
  }

  private adopt(incoming: Save): void {
    const store = saves();
    store.replace(adoptSave(store.save, incoming));
    this.pending = null;
    this.done = true;
    this.note = null;
    gameAudio.chime(true);
    haptics.impact();
    this.redraw();
  }
}
